import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingCart, MapPin, Clock, DollarSign, ArrowLeft, ArrowRight,
  CheckCircle, User, Phone, Plus, Trash2, AlertCircle, Store,
  Zap, Shield, Star, Package, ListChecks
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { toast } from 'sonner';

const ErrandBookingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState(null);
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    errandType: 'grocery',
    taskDescription: '',
    purchaseBudget: '',
    storeAddress: '',
    storeCity: '',
    storeName: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryZip: '',
    additionalStops: [],
    scheduledDate: '',
    scheduledTime: '',
    estimatedDuration: '1',
    specialInstructions: ''
  });

  const errandTypes = [
    { id: 'grocery', label: 'Grocery Shopping', icon: '🛒', description: 'Pick up groceries from any store', color: '#10B981' },
    { id: 'prescription', label: 'Prescription Pickup', icon: '💊', description: 'Pharmacy pickup (ID may be required)', color: '#EF4444' },
    { id: 'returns', label: 'Store Returns', icon: '📦', description: 'Return items to stores', color: '#F59E0B' },
    { id: 'documents', label: 'Document Pickup', icon: '📄', description: 'Pick up important papers', color: '#3B82F6' },
    { id: 'custom', label: 'Custom Errand', icon: '✨', description: 'Other approved tasks', color: '#8B5CF6' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addStop = () => {
    setFormData(prev => ({
      ...prev,
      additionalStops: [...prev.additionalStops, { address: '', city: '', storeName: '' }]
    }));
  };

  const removeStop = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalStops: prev.additionalStops.filter((_, i) => i !== index)
    }));
  };

  const updateStop = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      additionalStops: prev.additionalStops.map((stop, i) => 
        i === index ? { ...stop, [field]: value } : stop
      )
    }));
  };

  const calculateEstimate = async () => {
    setLoading(true);
    try {
      const response = await api.post('/pricing/estimate', {
        service_type: 'errands',
        errand_type: formData.errandType,
        duration_hours: parseFloat(formData.estimatedDuration),
        additional_stops: formData.additionalStops.length
      });
      setPriceEstimate(response.data);
      setStep(4);
    } catch (error) {
      const basePrice = 25;
      const hourlyRate = 20;
      const hours = parseFloat(formData.estimatedDuration) || 1;
      const stopsFee = formData.additionalStops.length * 5;
      setPriceEstimate({
        base_fare: basePrice,
        hourly_rate: hourlyRate,
        estimated_hours: hours,
        stops_fee: stopsFee,
        total: basePrice + (hourlyRate * hours) + stopsFee
      });
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const stopsText = formData.additionalStops.length > 0 
        ? `\n\nADDITIONAL STOPS:\n${formData.additionalStops.map((s, i) => 
            `${i + 1}. ${s.storeName || 'Stop'} - ${s.address}, ${s.city}`
          ).join('\n')}`
        : '';

      const response = await api.post('/checkout/guest/initiate', {
        service_type: 'errands',
        service_name: `${errandTypes.find(t => t.id === formData.errandType)?.label || 'Errand'}`,
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        address: formData.deliveryAddress,
        city: formData.deliveryCity,
        state: 'NC',
        zip_code: formData.deliveryZip,
        scheduled_date: formData.scheduledDate,
        scheduled_time: formData.scheduledTime,
        duration_hours: parseFloat(formData.estimatedDuration),
        special_instructions: `
ERRAND TYPE: ${errandTypes.find(t => t.id === formData.errandType)?.label}
TASK: ${formData.taskDescription}
${formData.purchaseBudget ? `PURCHASE BUDGET: $${formData.purchaseBudget}` : ''}

STORE/PICKUP LOCATION: ${formData.storeName || 'Store'}
${formData.storeAddress}, ${formData.storeCity}
${stopsText}

DELIVER TO: ${formData.deliveryAddress}, ${formData.deliveryCity}, NC ${formData.deliveryZip}

${formData.specialInstructions ? `SPECIAL INSTRUCTIONS: ${formData.specialInstructions}` : ''}
        `.trim(),
        total_amount: priceEstimate?.total || 45,
        payment_method: 'card'
      });

      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data?.pending_id) {
        navigate(`/guest-checkout?pending=${response.data.pending_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: 'Your Info', icon: User },
    { num: 2, label: 'Task', icon: ListChecks },
    { num: 3, label: 'Schedule', icon: Clock },
    { num: 4, label: 'Confirm', icon: CheckCircle }
  ];

  const selectedErrand = errandTypes.find(t => t.id === formData.errandType);

  return (
    <div className="min-h-screen service-bg-errands relative overflow-hidden">
      {/* Animated Background Glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-pink-500/15 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/3 left-1/4 w-[400px] h-[400px] bg-orange-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      {/* Header */}
      <div className="sticky-header px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="w-10 h-10 rounded-xl glass-card flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            data-testid="errands-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 rounded-2xl icon-gradient-errands flex items-center justify-center shadow-lg glow-errands">
            <ShoppingCart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white font-['Plus_Jakarta_Sans']">Errands</h1>
            <h1 className="text-xl font-bold text-white font-['Plus_Jakarta_Sans']">Service</h1>
          </div>
        </div>
      </div>

      {/* Premium Service Banner */}
      <div className="max-w-lg mx-auto px-4 mt-4 mb-6">
        <div className="glass-card-subtle p-4 flex items-center gap-3 border-pink-500/20">
          <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center">
            <Star className="w-5 h-5 text-pink-400" />
          </div>
          <div>
            <span className="text-pink-400 font-semibold">Personal Assistant</span>
            <span className="text-slate-400 ml-2">— We handle your tasks so you don't have to</span>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-lg mx-auto px-4 py-4">
        <div className="relative flex items-center justify-between">
          {/* Connecting Line */}
          <div className="step-connector" />
          <div 
            className="step-connector-active" 
            style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%`, background: 'linear-gradient(90deg, #EC4899, #F97316)' }} 
          />
          
          {steps.map((s, idx) => (
            <motion.div 
              key={s.num}
              className="flex flex-col items-center relative z-10"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.1 }}
            >
              <div 
                className={`step-node ${
                  step > s.num ? 'step-node-completed' : 
                  step === s.num ? 'bg-gradient-to-br from-pink-500 to-orange-500 text-white shadow-lg shadow-pink-500/40' : 'step-node-pending'
                }`}
                data-testid={`errands-step-${s.num}`}
              >
                {step > s.num ? <CheckCircle className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-lg mx-auto px-4 pb-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
            className="glass-card p-6 sm:p-8"
          >
            {/* Step 1: Customer Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl icon-gradient-errands flex items-center justify-center glow-errands">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-100 font-['Plus_Jakarta_Sans']">Your Information</h2>
                    <p className="text-slate-400 text-sm">Tell us how to reach you</p>
                  </div>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <label className="text-white/70 text-sm mb-2 block font-medium">Full Name *</label>
                    <Input
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl focus:border-[#EC4899] focus:ring-[#EC4899]/20 placeholder:text-white/50"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/70 text-sm mb-2 block font-medium">Phone *</label>
                      <Input
                        name="customerPhone"
                        value={formData.customerPhone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl focus:border-[#EC4899] placeholder:text-white/50"
                        data-testid="customer-phone-input"
                      />
                    </div>
                    <div>
                      <label className="text-white/70 text-sm mb-2 block font-medium">Email *</label>
                      <Input
                        name="customerEmail"
                        type="email"
                        value={formData.customerEmail}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl focus:border-[#EC4899] placeholder:text-white/50"
                        data-testid="customer-email-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex flex-wrap justify-center gap-6 text-white/40 text-xs">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#EC4899]" />
                      <span>Vetted Helpers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-[#F59E0B]" />
                      <span>Same Day Service</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-[#D4AF37]" />
                      <span>5-Star Rated</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Task Details */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#EC4899]/20 to-[#F59E0B]/20 flex items-center justify-center border border-[#EC4899]/30">
                    <ListChecks className="w-6 h-6 text-[#EC4899]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">What Do You Need?</h2>
                    <p className="text-white/50 text-sm">Tell us about your errand</p>
                  </div>
                </div>
                
                {/* Errand Type Selection */}
                <div>
                  <label className="text-white/70 text-sm mb-3 block font-medium">Type of Errand *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {errandTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setFormData(prev => ({ ...prev, errandType: type.id }))}
                        className={`p-4 rounded-2xl border text-left transition-all duration-300 ${
                          formData.errandType === type.id
                            ? 'border-[#EC4899] shadow-lg shadow-[#EC4899]/20'
                            : 'bg-white/[0.02] border-white/10 hover:border-white/30 hover:bg-white/[0.05]'
                        }`}
                        style={{
                          background: formData.errandType === type.id 
                            ? `linear-gradient(135deg, ${type.color}20, ${type.color}10)` 
                            : undefined
                        }}
                        data-testid={`errand-type-${type.id}`}
                      >
                        <span className="text-2xl block mb-2">{type.icon}</span>
                        <p className={`font-semibold text-sm ${formData.errandType === type.id ? 'text-white' : 'text-white/80'}`}>
                          {type.label}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Task Description */}
                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">Describe your task *</label>
                  <textarea
                    name="taskDescription"
                    value={formData.taskDescription}
                    onChange={handleChange}
                    placeholder="What items do you need? Any specific brands or details?"
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder:text-white/50 focus:border-[#EC4899] focus:outline-none resize-none"
                    data-testid="task-description-input"
                  />
                </div>

                {/* Purchase Budget */}
                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">
                    Purchase Budget (optional)
                    <span className="text-white/40 ml-2 font-normal">— If we need to buy items for you</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-[200px]">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50">$</span>
                      <Input
                        type="number"
                        name="purchaseBudget"
                        value={formData.purchaseBudget}
                        onChange={handleChange}
                        placeholder="0.00"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl pl-8 placeholder:text-white/50"
                        data-testid="purchase-budget-input"
                      />
                    </div>
                  </div>
                  <p className="text-white/40 text-xs mt-2">
                    Purchases are separate from the service fee. Receipts provided.
                  </p>
                </div>

                {/* Store Location */}
                <div className="bg-gradient-to-br from-[#EC4899]/10 to-[#F59E0B]/10 border border-[#EC4899]/30 rounded-2xl p-5">
                  <h3 className="text-[#EC4899] font-semibold mb-4 flex items-center gap-2">
                    <Store className="w-5 h-5" />
                    Store / Pickup Location
                  </h3>
                  <div className="space-y-3">
                    <Input
                      name="storeName"
                      value={formData.storeName}
                      onChange={handleChange}
                      placeholder="Store Name (e.g., Walmart, CVS)"
                      className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      data-testid="store-name-input"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        name="storeAddress"
                        value={formData.storeAddress}
                        onChange={handleChange}
                        placeholder="Address (optional)"
                        className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      />
                      <Input
                        name="storeCity"
                        value={formData.storeCity}
                        onChange={handleChange}
                        placeholder="City"
                        className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Stops */}
                {formData.additionalStops.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-white/60 text-sm font-medium">Additional Stops (+$5 each)</h4>
                    {formData.additionalStops.map((stop, idx) => (
                      <motion.div 
                        key={idx} 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex gap-2 items-center bg-white/5 p-3 rounded-xl border border-white/10"
                      >
                        <Input
                          value={stop.storeName}
                          onChange={(e) => updateStop(idx, 'storeName', e.target.value)}
                          placeholder="Store Name"
                          className="bg-black/30 border-white/10 text-white h-10 rounded-lg flex-1 placeholder:text-white/50"
                        />
                        <Input
                          value={stop.city}
                          onChange={(e) => updateStop(idx, 'city', e.target.value)}
                          placeholder="City"
                          className="bg-black/30 border-white/10 text-white h-10 rounded-lg w-32 placeholder:text-white/50"
                        />
                        <button 
                          onClick={() => removeStop(idx)} 
                          className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
                
                <Button
                  variant="outline"
                  onClick={addStop}
                  className="border-white/20 text-white hover:bg-white/10 rounded-xl"
                  data-testid="add-stop-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Stop (+$5)
                </Button>

                {/* Delivery Address */}
                <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#F4C2C2]/10 border border-[#D4AF37]/30 rounded-2xl p-5">
                  <h3 className="text-[#D4AF37] font-semibold mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Deliver To
                  </h3>
                  <div className="space-y-3">
                    <Input
                      name="deliveryAddress"
                      value={formData.deliveryAddress}
                      onChange={handleChange}
                      placeholder="Street Address"
                      className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      data-testid="delivery-address-input"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        name="deliveryCity"
                        value={formData.deliveryCity}
                        onChange={handleChange}
                        placeholder="City"
                        className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      />
                      <Input
                        name="deliveryZip"
                        value={formData.deliveryZip}
                        onChange={handleChange}
                        placeholder="ZIP Code"
                        className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Schedule */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#EC4899]/20 to-[#F59E0B]/20 flex items-center justify-center border border-[#EC4899]/30">
                    <Clock className="w-6 h-6 text-[#EC4899]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">When Do You Need This?</h2>
                    <p className="text-white/50 text-sm">Pick a time that works for you</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/70 text-sm mb-2 block font-medium">Date *</label>
                    <Input
                      type="date"
                      name="scheduledDate"
                      value={formData.scheduledDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl [color-scheme:dark]"
                      data-testid="scheduled-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-white/70 text-sm mb-2 block font-medium">Preferred Time *</label>
                    <Input
                      type="time"
                      name="scheduledTime"
                      value={formData.scheduledTime}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl [color-scheme:dark]"
                      data-testid="scheduled-time-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">Estimated Duration</label>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { value: '0.5', label: '30 min' },
                      { value: '1', label: '1 hour' },
                      { value: '1.5', label: '1.5 hrs' },
                      { value: '2', label: '2 hours' },
                      { value: '3', label: '3 hours' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setFormData(prev => ({ ...prev, estimatedDuration: option.value }))}
                        className={`p-3 rounded-xl border text-center transition-all ${
                          formData.estimatedDuration === option.value
                            ? 'bg-gradient-to-br from-[#EC4899]/20 to-[#F59E0B]/20 border-[#EC4899] text-white'
                            : 'bg-white/5 border-white/10 text-white/60 hover:border-white/30'
                        }`}
                        data-testid={`duration-${option.value}`}
                      >
                        <span className="text-sm font-medium">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">Special Instructions (optional)</label>
                  <textarea
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleChange}
                    placeholder="Any additional notes for our helper..."
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder:text-white/50 focus:border-[#EC4899] focus:outline-none resize-none"
                    data-testid="special-instructions-input"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Confirm */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#EC4899]/20 to-[#F59E0B]/20 flex items-center justify-center border border-[#EC4899]/30">
                    <CheckCircle className="w-6 h-6 text-[#EC4899]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Confirm Your Errand</h2>
                    <p className="text-white/50 text-sm">Review your order details</p>
                  </div>
                </div>
                
                {/* Price Summary Card */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#EC4899]/20 via-[#F59E0B]/10 to-[#EC4899]/20 border border-[#EC4899]/30 rounded-2xl p-6">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#EC4899]/20 rounded-full blur-3xl" />
                  <h3 className="text-lg font-bold text-white mb-4 relative z-10">Service Fee Estimate</h3>
                  <div className="space-y-3 text-white/80 relative z-10">
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4 text-[#EC4899]" />
                        Base Fee
                      </span>
                      <span className="font-semibold">${priceEstimate?.base_fare?.toFixed(2) || '25.00'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#F59E0B]" />
                        Time ({formData.estimatedDuration} hr)
                      </span>
                      <span className="font-semibold">${((priceEstimate?.hourly_rate || 20) * parseFloat(formData.estimatedDuration || 1)).toFixed(2)}</span>
                    </div>
                    {formData.additionalStops.length > 0 && (
                      <div className="flex justify-between items-center text-[#D4AF37]">
                        <span className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Additional Stops ({formData.additionalStops.length})
                        </span>
                        <span className="font-semibold">+${(formData.additionalStops.length * 5).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-white/20 pt-3 mt-3 flex justify-between items-center">
                      <span className="text-xl font-bold text-white">Service Total</span>
                      <span className="text-2xl font-bold bg-gradient-to-r from-[#EC4899] to-[#F59E0B] bg-clip-text text-transparent">
                        ${priceEstimate?.total?.toFixed(2) || '45.00'}
                      </span>
                    </div>
                  </div>
                  {formData.purchaseBudget && (
                    <p className="text-white/60 text-sm mt-4 relative z-10">
                      + Up to ${formData.purchaseBudget} for purchases (charged separately based on receipts)
                    </p>
                  )}
                </div>

                {/* Order Summary */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${selectedErrand?.color}20` }}>
                      <span className="text-xl">{selectedErrand?.icon}</span>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Task</p>
                      <p className="text-white font-medium">{selectedErrand?.label}</p>
                      <p className="text-white/60 text-sm mt-1">{formData.taskDescription}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#EC4899]/20 flex items-center justify-center flex-shrink-0">
                      <Store className="w-5 h-5 text-[#EC4899]" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Store</p>
                      <p className="text-white font-medium">{formData.storeName || 'TBD'}</p>
                      <p className="text-white/60 text-sm">{formData.storeCity}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Deliver To</p>
                      <p className="text-white font-medium">{formData.deliveryAddress}</p>
                      <p className="text-white/60 text-sm">{formData.deliveryCity}, NC</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-white/70" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">When</p>
                      <p className="text-white font-medium">{formData.scheduledDate} at {formData.scheduledTime}</p>
                    </div>
                  </div>
                </div>

                <p className="text-white/40 text-sm text-center">
                  You'll be redirected to secure payment after confirming.
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="border-white/20 text-white hover:bg-white/10 rounded-xl px-6"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  className="bg-gradient-to-r from-[#EC4899] to-[#F59E0B] hover:from-[#DB2777] hover:to-[#D97706] text-white rounded-xl px-6 shadow-lg shadow-[#EC4899]/30"
                  disabled={
                    (step === 1 && (!formData.customerName || !formData.customerPhone || !formData.customerEmail)) ||
                    (step === 2 && (!formData.taskDescription || !formData.deliveryAddress))
                  }
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : step === 3 ? (
                <Button
                  onClick={calculateEstimate}
                  className="bg-gradient-to-r from-[#EC4899] to-[#F59E0B] hover:from-[#DB2777] hover:to-[#D97706] text-white rounded-xl px-6 shadow-lg shadow-[#EC4899]/30"
                  disabled={loading || !formData.scheduledDate || !formData.scheduledTime}
                  data-testid="get-price-btn"
                >
                  {loading ? 'Calculating...' : 'Get Price'}
                  <DollarSign className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] hover:from-[#E6C965] hover:to-[#F4C2C2] text-black font-bold rounded-xl px-8 shadow-lg shadow-[#D4AF37]/30"
                  disabled={loading}
                  data-testid="book-pay-btn"
                >
                  {loading ? 'Processing...' : 'Book & Pay'}
                  <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ErrandBookingPage;
